import { Emoji, EmojiPicker, useFetch, useFormContext } from "@4uhub/lib4uhub";
import { Box, TextField } from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { TBodyInput } from "./models";
import { TextFormatActions } from "./TextFormatActions/TextFormatActions";
import { VariablesExamples } from "./VariablesExamples/VariablesExamples";
import { CURLY_BRACKETS_VALIDATOR } from "../../../../utils/Validators/regexValidators";
import { REMOVE_CURLY_BRACKETS } from "../../../../utils/conversions";
import { useFieldArray, useWatch } from "react-hook-form";
import { useTemplateWhats } from "../../../../store/contexts/TemplateWhatsContext";
import { AddVariablesButtonGeneric } from "./AddVariablesButtonGeneric/AddVariablesButtonGeneric";
import { listTicketAutomationMacros } from "../../../../services/ticketAutomationMacro.service";
import { ITicketAutomationMacro } from "../../../../models/ticket-automation-macro";
import { listVariablesInternalTemplateField } from "../../../../services/ticketAutomationInternalTemplateField.service";
import { TWhatsappTemplateForm } from "../../../../screens/WhatsappTemplate/WhatsappTemplateForm/whatsappTemplateSchema";

const translationPath = "components.bodyInput.";

export const BodyInput = memo(
  ({
    automationId,
    internalTemplateId,
    componentType,
    field,
    error,
    ...otherProps
  }: TBodyInput) => {
    const { t } = useTranslation();

    const { setError, clearErrors, control, setValue, getValues } =
      useFormContext<TWhatsappTemplateForm>();

    const { templateWhatsBasicData } = useTemplateWhats();

    const [options, setOptions] = useState<ITicketAutomationMacro[]>([]);
    const [message, setMessage] = useState<string>(
      field.value || templateWhatsBasicData.body || "",
    );
    const [variables, setVariables] = useState<string[]>([]);
    const [cursorPosition, setCursorPosition] = useState<number | null>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    const { sendRequest } = useFetch(listTicketAutomationMacros);
    const { sendRequest: sendRequestInternalTemplate } = useFetch(
      listVariablesInternalTemplateField,
    );

    const { remove: removeBodyExample } = useFieldArray({
      name: "bodyExamples",
      control,
    });

    const {
      fields: characterLimitFields,
      append: appendCharacterLimit,
      remove: removeCharacterLimit,
    } = useFieldArray({
      name: "characterLimitRequests",
      control,
    });

    const characterLimitValues = useWatch({
      name: "characterLimitRequests",
      control,
    });

    const prevCharLimitsRef = useRef<any[] | null>(null);

    const fetchVariablesHandler = useCallback(async () => {
      if (!internalTemplateId || internalTemplateId.length === 0) {
        if (!automationId || automationId.length === 0) return;
        const { data, success } = await sendRequest({
          TicketAutomationId: automationId,
          ComponentType: componentType,
        });
        if (data && success) setOptions(data);
      } else {
        const { data, success } = await sendRequestInternalTemplate({
          TicketAutomationInternalTemplateId: internalTemplateId,
          TicketAutomationId: automationId,
        });
        if (data && success) setOptions(data);
      }
    }, [
      sendRequest,
      sendRequestInternalTemplate,
      automationId,
      internalTemplateId,
      componentType,
    ]);

    const handleEmojiClick = useCallback(
      (emoji: Emoji) => {
        const ref = inputRef.current;
        if (ref) {
          const start = ref.selectionStart ?? 0;
          const end = ref.selectionEnd ?? 0;
          const text =
            message.slice(0, start) + emoji.native + message.slice(end);
          setMessage(text);
          setCursorPosition(start + emoji.native.length);
        }
      },
      [message],
    );

    const handleEmojiOpen = useCallback(() => {
      inputRef.current?.focus();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const ref = inputRef.current;
      if (ref) {
        const start = ref.selectionStart ?? 0;
        setMessage(e.target.value);
        setCursorPosition(start);
      } else {
        setMessage(e.target.value);
      }
    };

    const addVariableHandler = useCallback(
      (variable: string) => {
        const ref = inputRef.current;
        if (ref) {
          const start = ref.selectionStart ?? 0;
          const end = ref.selectionEnd ?? 0;
          const text =
            message.slice(0, start) + `{{${variable}}}` + message.slice(end);
          setMessage(text);
          setCursorPosition(start + variable.length);
        }
      },
      [message],
    );

    useEffect(() => {
      if (inputRef.current && cursorPosition !== null) {
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        setCursorPosition(null);
      }
    }, [cursorPosition]);

    useEffect(() => {
      field.onChange(message);
    }, [field, message]);

    useEffect(() => {
      for (let i = (characterLimitFields?.length ?? 0) - 1; i >= 0; i--) {
        const f: any = characterLimitFields[i];
        if (!f?.ticketAutomationMacroId && !f?.name) {
          removeCharacterLimit(i);
        }
      }
    }, [characterLimitFields, removeCharacterLimit]);

    const normalize = (s?: string | null) =>
      (s ?? "").replace(REMOVE_CURLY_BRACKETS, "").trim();

    const defaultLimitByName = useMemo(() => {
      const map = new Map<string, string>();

      (characterLimitValues ?? []).forEach((it: any) => {
        const n = normalize(it?.name);
        const raw = `${it?.characterLimit ?? ""}`.trim();
        if (n && raw !== "" && !map.has(n)) map.set(n, raw);
      });

      (options ?? []).forEach((o) => {
        const n = normalize(o?.name);
        const has = map.has(n);
        if (!n || has) return;
        if (o?.characterLimit != null) {
          map.set(n, String(o.characterLimit));
        }
      });

      return map;
    }, [characterLimitValues, options]);

    const onAddCharacterLimitRequestHandler = useCallback(
      (variable: ITicketAutomationMacro) => {
        const norm = (s?: string | null) =>
          (s ?? "").replace(REMOVE_CURLY_BRACKETS, "").trim();

        const sameIdx = (characterLimitValues ?? []).findIndex(
          (it: any) => norm(it?.name) === norm(variable.name),
        );

        const inherited =
          sameIdx >= 0
            ? String(characterLimitValues?.[sameIdx]?.characterLimit ?? "")
            : "";

        const fallback =
          defaultLimitByName.get(norm(variable.name)) ??
          (variable.characterLimit != null
            ? String(variable.characterLimit)
            : "0");

        appendCharacterLimit({
          ticketAutomationMacroId: variable.id,
          name: variable.name,
          characterLimit: inherited !== "" ? inherited : fallback,
        });
      },
      [appendCharacterLimit, characterLimitValues, defaultLimitByName],
    );

    useEffect(() => {
      const matches = message.match(CURLY_BRACKETS_VALIDATOR);
      const foundVars = matches ? matches.map((m) => m) : [];

      setVariables((oldVariables) => {
        oldVariables.forEach((v, i) => {
          if (!foundVars.includes(v)) removeBodyExample(i);
        });
        return foundVars;
      });

      const needCountByName = new Map<string, number>();
      for (const v of foundVars) {
        const n = normalize(v);
        if (!n) continue;
        needCountByName.set(n, (needCountByName.get(n) ?? 0) + 1);
      }

      const indicesByName = new Map<string, number[]>();
      (characterLimitFields ?? []).forEach((f: any, idx: number) => {
        const n = normalize(f?.name);
        if (!n) return;
        if (!indicesByName.has(n)) indicesByName.set(n, []);
        indicesByName.get(n)!.push(idx);
      });

      const optionByName = new Map(
        (options ?? [])
          .filter((o) => o && typeof o.name === "string" && o.name.length > 0)
          .map((o) => [normalize(o.name), o] as const),
      );

      needCountByName.forEach((need, name) => {
        const opt = optionByName.get(name);
        if (!opt) return;

        const have = indicesByName.get(name)?.length ?? 0;

        if (need > have) {
          const existingIdxs = indicesByName.get(name) ?? [];
          const inheritedLimit =
            existingIdxs.length > 0
              ? String(
                  (
                    characterLimitValues?.[existingIdxs[0]]?.characterLimit ??
                    ""
                  ).toString(),
                )
              : "";

          const fallbackFromMap = defaultLimitByName.get(name);
          const fallbackFromOpt =
            opt && opt.characterLimit != null
              ? String(opt.characterLimit)
              : "0";

          const charLimitToUse =
            inheritedLimit !== ""
              ? inheritedLimit
              : (fallbackFromMap ?? fallbackFromOpt);

          for (let i = 0; i < need - have; i++) {
            appendCharacterLimit({
              ticketAutomationMacroId: opt?.id ?? "",
              name: opt?.name ?? name,
              characterLimit: charLimitToUse,
            });
          }
        } else if (have > need) {
          const idxs = [...(indicesByName.get(name) ?? [])].sort(
            (a, b) => b - a,
          );
          for (let i = 0; i < have - need; i++) {
            const toRemove = idxs[i];
            if (toRemove != null) removeCharacterLimit(toRemove);
          }
        }
      });

      indicesByName.forEach((idxs, name) => {
        if (!needCountByName.has(name)) {
          const sorted = [...idxs].sort((a, b) => b - a);
          for (const idx of sorted) removeCharacterLimit(idx);
        }
      });
    }, [
      message,
      options,
      characterLimitFields,
      characterLimitValues,
      appendCharacterLimit,
      removeCharacterLimit,
      removeBodyExample,
      defaultLimitByName,
    ]);

    useEffect(() => {
      if (variables.length === 0) removeBodyExample(0);
    }, [variables, removeBodyExample]);

    useEffect(() => {
      const optionsNames = options.map((option) =>
        option.name.replace(REMOVE_CURLY_BRACKETS, ""),
      );

      const hasInvalidVariable =
        variables.length > 0 &&
        variables.some(
          (v) => !optionsNames.includes(v.replace(REMOVE_CURLY_BRACKETS, "")),
        );

      if (hasInvalidVariable) {
        setError(field.name, {
          type: "pattern",
          message: `${t(translationPath + "error")}`,
        });

        setValue("invalidVariables", true, {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: false,
        });
      } else {
        clearErrors(field.name);
        setValue("invalidVariables", false, {
          shouldDirty: true,
          shouldTouch: false,
          shouldValidate: false,
        });
      }
    }, [variables, options, field.name, setError, clearErrors, setValue, t]);

    useEffect(() => {
      fetchVariablesHandler();
    }, [fetchVariablesHandler]);

    useEffect(() => {
      if (otherProps.inputProps?.maxLength) {
        const messageUpdate = message.slice(
          0,
          otherProps.inputProps?.maxLength,
        );
        setMessage(messageUpdate);
      }
    }, [otherProps.inputProps?.maxLength]);

    useEffect(() => {
      setMessage((prevState) => {
        if (
          templateWhatsBasicData.body === "" ||
          !templateWhatsBasicData.body
        ) {
          return templateWhatsBasicData.body;
        }
        return prevState;
      });
    }, [templateWhatsBasicData.body, setMessage]);

    useEffect(() => {
      const prev = prevCharLimitsRef.current;
      const curr = characterLimitValues ?? [];

      let changedIndex = -1;
      if (prev && prev.length === curr.length) {
        for (let i = 0; i < curr.length; i++) {
          const prevVal = `${prev[i]?.characterLimit ?? ""}`;
          const currVal = `${curr[i]?.characterLimit ?? ""}`;
          if (prevVal !== currVal) {
            changedIndex = i;
            break;
          }
        }
      }

      if (changedIndex === -1) {
        prevCharLimitsRef.current = curr
          ? JSON.parse(JSON.stringify(curr))
          : [];
        return;
      }

      const changedItem = curr[changedIndex];
      const macroId = changedItem?.ticketAutomationMacroId;
      const newLimit = changedItem?.characterLimit;

      if (macroId == null) {
        prevCharLimitsRef.current = JSON.parse(JSON.stringify(curr));
        return;
      }

      for (let i = 0; i < curr.length; i++) {
        if (i === changedIndex) continue;
        const it = curr[i];
        if (
          it?.ticketAutomationMacroId === macroId &&
          `${it?.characterLimit ?? ""}` !== `${newLimit ?? ""}`
        ) {
          setValue(`characterLimitRequests.${i}.characterLimit`, newLimit, {
            shouldDirty: true,
            shouldTouch: false,
          });
        }
      }

      prevCharLimitsRef.current = JSON.parse(
        JSON.stringify(getValues("characterLimitRequests") ?? []),
      );
    }, [characterLimitValues, setValue, getValues]);

    return (
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "end",
        }}
      >
        <TextField
          {...field}
          onChange={handleChange}
          value={message}
          error={!!error}
          {...otherProps}
          helperText={error ? t(error?.message || "") : ""}
          inputRef={inputRef}
        />

        <Box sx={{ display: "flex" }}>
          <EmojiPicker
            size="small"
            onEmojiClick={handleEmojiClick}
            onEmojiOpen={handleEmojiOpen}
          />
          <TextFormatActions
            inputRef={inputRef}
            message={message}
            setMessage={setMessage}
          />
          <AddVariablesButtonGeneric
            onAddVariable={addVariableHandler}
            onAddCharacterLimitRequest={onAddCharacterLimitRequestHandler}
            inputRef={inputRef}
            options={options}
          />
        </Box>

        {variables && variables.length > 0 && !error && (
          <VariablesExamples
            variables={variables}
            characterLimitRequests={characterLimitFields}
          />
        )}
      </Box>
    );
  },
);
